/**
 * Custom Axios instance used by the orval-generated client.
 *
 * Configured once here so every generated hook shares the same base URL,
 * timeout, and default headers. Replace baseURL at runtime via an env var
 * when needed (e.g. during Playwright e2e runs against a specific host).
 */
import axios from "axios";
import type { AxiosRequestConfig } from "axios";

export const axiosInstance = axios.create({
  // The OpenAPI server base path is `/api` (the API owns it via UsePathBase), but the
  // orval-generated operation paths are relative to it (e.g. `/hospitals`), so the
  // `/api` prefix must live here. In production the SPA and the API share an origin and
  // Traefik path-routes `/api` → the API; in local dev Vite's proxy forwards `/api` →
  // the backend. Either way every generated call resolves to `/api/<operation>`.
  baseURL: "/api",
  timeout: 15_000,
  headers: {
    "Content-Type": "application/json",
  },
});

/**
 * Orval mutator signature. Every generated request goes through this
 * function so we have a single integration point for auth headers, error
 * normalisation, or base-URL overrides.
 */
export const customAxiosInstance = <T>(
  config: AxiosRequestConfig,
  options?: AxiosRequestConfig,
): Promise<T> => {
  const source = axios.CancelToken.source();
  const promise = axiosInstance<T>({
    ...config,
    ...options,
    cancelToken: source.token,
  }).then(({ data }) => data);

  // Orval passes a `signal` via options; bridge it to axios CancelToken
  const signal = options?.signal as AbortSignal | undefined;
  if (signal) {
    signal.addEventListener("abort", () => {
      source.cancel("Query was aborted.");
    });
  }

  return promise;
};
